// Turns a failed workspace-status fetch into what kore's ErrorPage needs: a
// status for its badge, plus the title/detail to show underneath.
//
// The server's RFC 9457 title is the primary source of truth — for most
// errors (403 "not allowed", 404 "workspace not found") it is the only
// human-readable field the server sends, `detail` is frequently absent. The
// hand-written copy below is only a fallback for the two cases where there
// is no server title to show: the failure never reached the API at all (a
// connectivity problem — not an ApiError), or it came back as an opaque,
// non-JSON body (an intermediary's error page, not ewt-server's).

import type { ErrorPageStatus } from "@pej/kore/components";
import { extractStatus } from "@pej/kore/utils";
import { ApiError } from "@/api/error";

export interface StatusErrorDescription {
    status: ErrorPageStatus;
    pd: {
        title: string;
        detail?: string;
        requestId?: string;
    };
}

// Keyed on kore's dedicated statuses; any other status ("409", "503") takes
// the generic copy.
type Dedicated = "403" | "404" | "OFFLINE" | "EXPIRED";

const GENERIC = {
    title: "Something went wrong",
    detail: "Try again in a moment.",
};

const FALLBACK: Record<Dedicated, { title: string; detail: string }> = {
    "403": {
        title: "You don't have access to this workspace",
        detail: "Ask whoever manages it to add you, then try again.",
    },
    "404": {
        title: "This workspace doesn't exist",
        detail: "Check the link, or ask whoever shared it with you.",
    },
    OFFLINE: {
        title: "Can't connect right now",
        detail: "Check your connection, then try again.",
    },
    // Reached only after the SSO round-trip ran once (see api/reauth.ts).
    EXPIRED: {
        title: "You've been signed out",
        detail: "Sign in again to continue.",
    },
};

function fallbackFor(status: ErrorPageStatus): { title: string; detail: string } {
    return Object.hasOwn(FALLBACK, status) ? FALLBACK[status as Dedicated] : GENERIC;
}

export function describeStatusError(error: unknown): StatusErrorDescription {
    if (error instanceof ApiError) {
        // kore's own mapping, as on its error page: the error's own 4xx/5xx,
        // 401 as EXPIRED, dedicated 403/404.
        const status = extractStatus(error);
        if (error.title) {
            return { status, pd: { title: error.title, detail: error.detail, requestId: error.requestId } };
        }
        return { status, pd: fallbackFor(status) };
    }
    // Not an ApiError: fetch itself rejected (offline, DNS, a cross-origin
    // redirect with no CORS headers) before any response came back.
    return { status: "OFFLINE", pd: FALLBACK.OFFLINE };
}
